import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BottleDTO, DrinkProfile } from '@adega/shared';
import { chaveDoVinho, idadeSemAnaliseQuePedeAtencao } from '@adega/shared';
import { BottleCard } from '../components/BottleCard.js';
import { AiConfigBanner, EmptyState, ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import { useAiStatus, useAnalyzeWindows, useCellars, useDrinkFirst } from '../api/queries.js';

/**
 * Ordem de consumo de todas as adegas.
 *
 * A IA estima janela e pico uma vez (botão "Analisar"), e a ordem é calculada
 * pelo backend em cima do que foi salvo: a lista não muda a cada abertura e não
 * gasta IA para ser exibida.
 */
/**
 * Vinho de beber jovem não disputa atenção com vinho de guarda.
 *
 * A tela responde **"o que abrir para não perder o momento"**, e vinho do dia a
 * dia não tem momento a perder. Com 21 deles no topo de "No auge (37)", as 16
 * garrafas que de fato têm um platô para acertar ficavam abaixo da dobra.
 *
 * O raciocínio que essa separação corrige: a velocidade de queda só importa se
 * houver algo de valor para cair. Um branco aromático perdendo frescor é uma
 * perda pequena numa coisa pequena; perder o platô de um vinho guardado é perder
 * exatamente aquilo que se estava guardando.
 *
 * **Prazo vence perfil.** Garrafa de beber jovem cuja janela fecha este ano (ou
 * já fechou) continua no topo da lista principal: aí a perda deixou de ser
 * hipótese. Só as que estão dentro da janela descem.
 */
const NIVEIS_COM_PRAZO = ['beber_agora', 'beber_este_ano'];

interface GrupoDeUrgencia {
  level: string;
  label: string;
  description: string;
  bottles: BottleDTO[];
}

function separarDoDiaADia(grupos: GrupoDeUrgencia[]): {
  comMomento: GrupoDeUrgencia[];
  doDiaADia: BottleDTO[];
} {
  const doDiaADia: BottleDTO[] = [];

  const comMomento = grupos
    .map((grupo) => {
      if (NIVEIS_COM_PRAZO.includes(grupo.level)) return grupo;

      const ficam: BottleDTO[] = [];
      for (const garrafa of grupo.bottles) {
        if (garrafa.drinkProfile === 'beber_jovem') doDiaADia.push(garrafa);
        else ficam.push(garrafa);
      }
      return { ...grupo, bottles: ficam };
    })
    .filter((grupo) => grupo.bottles.length > 0);

  // Entre os do dia a dia, safra mais antiga primeiro: nenhum deles tem prazo,
  // mas o de 2020 é o que menos ganha continuando parado.
  doDiaADia.sort((a, b) => (a.vintage ?? 9999) - (b.vintage ?? 9999));

  return { comMomento, doDiaADia };
}

type Perfil = DrinkProfile | 'sem_perfil';

/**
 * Subgrupos dentro de um balde de urgência.
 *
 * A janela é um penhasco: diz **quando** acaba. O perfil é a ladeira: diz **a
 * que velocidade** o vinho cai. Os dois não competem, se encaixam — garrafa com
 * a janela fechando este ano já sobe sozinha para "Beba este ano". Então dentro
 * de um balde ninguém tem prazo apertado, e a única pergunta que sobra é quanto
 * se perde esperando. Essa é a ladeira.
 *
 * Existia sem isto o balde "No auge" com 30 garrafas: uma pilha, não um
 * conselho. Dividido, vira "15 que só perdem daqui pra frente" e "2 que podem
 * esperar".
 *
 * **A ordem é o inverso da fila de espera, de propósito.** Lá a pergunta é quem
 * sofre mais fora da adega, e quem sofre mais é a guarda longa. Aqui a pergunta
 * é quem perde mais ficando fechada, e quem perde mais é o vinho jovem. Mesmos
 * três grupos, perguntas opostas.
 */
const ORDEM_DOS_PERFIS: Perfil[] = ['beber_jovem', 'guarda_curta', 'guarda_longa', 'sem_perfil'];

const TITULO_DO_PERFIL: Record<Perfil, string> = {
  beber_jovem: 'Para beber jovem',
  guarda_curta: 'Guarda curta',
  guarda_longa: 'Guarda longa',
  sem_perfil: 'Sem classificação',
};

const MOTIVO_DO_PERFIL: Record<Perfil, string> = {
  beber_jovem: 'Caem rápido. Cada ano parado custa caro a elas.',
  guarda_curta: 'Seguram o platô por alguns anos ainda.',
  guarda_longa: 'Estão boas agora e continuam boas. Podem esperar.',
  sem_perfil: 'Sem análise não dá para saber a que velocidade caem.',
};

/**
 * Abaixo disto a lista inteira se lê de uma vez, e título vira móvel na sala:
 * dois subgrupos de uma garrafa cada custam mais atenção do que economizam.
 */
const MINIMO_PARA_SUBDIVIDIR = 6;

function subdividirPorPerfil(
  garrafas: BottleDTO[],
): { perfil: Perfil; garrafas: BottleDTO[] }[] | null {
  if (garrafas.length < MINIMO_PARA_SUBDIVIDIR) return null;

  const blocos = ORDEM_DOS_PERFIS.map((perfil) => ({
    perfil,
    // O filtro preserva a ordem que veio do backend: o subgrupo reorganiza a
    // leitura, nunca a prioridade calculada dentro dele.
    garrafas: garrafas.filter((garrafa) => (garrafa.drinkProfile ?? 'sem_perfil') === perfil),
  })).filter((bloco) => bloco.garrafas.length > 0);

  return blocos.length > 1 ? blocos : null;
}

export function DrinkFirst() {
  const { data: grupos, isLoading, error } = useDrinkFirst();
  const { data: aiStatus } = useAiStatus();
  const { data: adegas } = useCellars();
  const semAdegas = (adegas?.length ?? 0) === 0;
  const analisar = useAnalyzeWindows();
  const [confirmandoReanalise, setConfirmandoReanalise] = useState(false);
  const [mostrarDoDiaADia, setMostrarDoDiaADia] = useState(false);

  if (isLoading) return <Loading />;

  const temGarrafas = grupos && grupos.length > 0;
  const totalGarrafas = grupos?.reduce((soma, g) => soma + g.bottles.length, 0) ?? 0;
  const garrafasSemAnalise = grupos?.find((g) => g.level === 'sem_informacao')?.bottles ?? [];
  const semAnalise = garrafasSemAnalise.length;
  // As antigas sem análise são as que mais podem estar passando do ponto, e
  // justamente elas ficam no fim da lista: vale dizer isso no convite a analisar.
  const antigasSemAnalise = garrafasSemAnalise.filter(
    (garrafa) => idadeSemAnaliseQuePedeAtencao(garrafa) !== null,
  ).length;
  const resultado = analisar.data;
  const { comMomento, doDiaADia } = separarDoDiaADia(grupos ?? []);

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Beber antes"
        subtitulo={
          semAdegas
            ? 'Suas garrafas, do que pede para abrir antes ao que pode esperar'
            : 'Todas as adegas, do que pede para abrir antes ao que pode esperar'
        }
      />

      {error ? <ErrorBox erro={error} /> : null}

      {/* Sem conexão não é adega vazia. Com erro na tela, "nada para ordenar"
          contradizia o aviso logo acima e ainda convidava a cadastrar, que é a
          única coisa que com certeza não vai funcionar agora. */}
      {!error && !temGarrafas ? (
        <EmptyState
          titulo="Nada para ordenar ainda"
          descricao="Cadastre garrafas para ver a ordem de consumo."
          acao={
            <Link to="/adicionar" className="btn-primary">
              Adicionar garrafa
            </Link>
          }
        />
      ) : null}

      {temGarrafas ? (
        <section className="card space-y-3">
          {aiStatus?.enabled ? (
            <>
              <div>
                {/* "da adega" não quer dizer nada para quem não tem uma. */}
                <p className="font-semibold">{semAdegas ? 'Análise das garrafas' : 'Análise da adega'}</p>
                <p className="text-sm text-adega-muted">
                  {semAnalise > 0
                    ? `${semAnalise} de ${totalGarrafas} garrafas ainda sem janela de consumo. A IA estima janela e ano de pico de todas numa única análise.`
                    : 'Todas as garrafas já têm janela de consumo. Reanalise de vez em quando — uma vez por ano costuma bastar.'}
                </p>
                {antigasSemAnalise > 0 ? (
                  <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                    {antigasSemAnalise === 1
                      ? '1 delas tem safra antiga para o estilo e pode já estar no fim da janela.'
                      : `${antigasSemAnalise} delas têm safra antiga para o estilo e podem já estar no fim da janela.`}{' '}
                    Sem análise, ficam no fim desta lista.
                  </p>
                ) : null}
              </div>

              {semAnalise > 0 ? (
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={analisar.isPending}
                  onClick={() => void analisar.mutateAsync('sem_informacao')}
                >
                  {analisar.isPending
                    ? `Analisando ${semAnalise} ${semAnalise === 1 ? 'garrafa' : 'garrafas'}…`
                    : `🤖 Analisar ${semAnalise === 1 ? 'a garrafa' : `as ${semAnalise} garrafas`} sem janela`}
                </button>
              ) : null}

              {/* Reanalisar tudo gasta mais e sobrescreve estimativas: pede confirmação. */}
              {confirmandoReanalise ? (
                <div className="space-y-2 rounded-xl bg-adega-surface-2 p-3">
                  <p className="text-sm">
                    Reanalisar as {totalGarrafas} garrafas substitui as janelas atuais pelas novas
                    estimativas. Garrafas que a IA não conseguir avaliar mantêm o que já tinham.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setConfirmandoReanalise(false)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={analisar.isPending}
                      onClick={async () => {
                        setConfirmandoReanalise(false);
                        await analisar.mutateAsync('todas');
                      }}
                    >
                      Reanalisar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={semAnalise > 0 ? 'btn-ghost w-full text-xs' : 'btn-secondary w-full'}
                  disabled={analisar.isPending}
                  onClick={() => setConfirmandoReanalise(true)}
                >
                  {analisar.isPending && semAnalise === 0
                    ? 'Reanalisando…'
                    : `Reanalisar todas as ${totalGarrafas} garrafas`}
                </button>
              )}

              {resultado && !analisar.isPending ? (
                <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
                  {resultado.enviadas === 0
                    ? 'Nenhuma garrafa precisava de análise.'
                    : `${resultado.analisadas} ${resultado.analisadas === 1 ? 'garrafa analisada' : 'garrafas analisadas'}` +
                      (resultado.copiadasDeIguais > 0
                        ? ` · ${resultado.copiadasDeIguais} ${resultado.copiadasDeIguais === 1 ? 'recebeu' : 'receberam'} a análise de uma garrafa igual, sem custo`
                        : '') +
                      (resultado.semDadosSuficientes > 0
                        ? ` · ${resultado.semDadosSuficientes} sem informação suficiente para estimar`
                        : '') +
                      '.'}
                </p>
              ) : null}

              {analisar.error ? <ErrorBox erro={analisar.error} /> : null}
            </>
          ) : (
            <AiConfigBanner contexto="A estimativa de janela e pico depende da IA." />
          )}
        </section>
      ) : null}

      {comMomento.map((grupo) => (
        <section key={grupo.level} className="space-y-2">
          <div>
            <h2 className="font-semibold">
              {grupo.label} ({grupo.bottles.length})
            </h2>
            <p className="text-xs text-adega-muted">{grupo.description}</p>
          </div>

          {(() => {
            const blocos = subdividirPorPerfil(grupo.bottles);
            if (blocos === null) return <Garrafas garrafas={grupo.bottles} />;

            return blocos.map((bloco) => (
              <div key={bloco.perfil} className="space-y-2 pt-1">
                <div className="border-l-2 border-adega-gold/40 pl-3">
                  <h3 className="text-sm font-semibold text-adega-gold">
                    {TITULO_DO_PERFIL[bloco.perfil]} ({bloco.garrafas.length})
                  </h3>
                  <p className="text-xs text-adega-muted">{MOTIVO_DO_PERFIL[bloco.perfil]}</p>
                </div>
                <Garrafas garrafas={bloco.garrafas} />
              </div>
            ));
          })()}
        </section>
      ))}

      {/*
        "Do dia a dia" nasce recolhido para não disputar atenção com quem tem
        prazo. Mas quando NÃO HÁ ninguém com prazo, recolher o único grupo é
        esconder tudo: a pessoa abre "Beber antes", vê um título fechado e
        conclui que não tem vinho nenhum cadastrado.

        Apareceu com o caminho novo (quem entra sem adega, com duas ou três
        garrafas do dia a dia), mas não é exclusivo dele: aconteceria com
        qualquer adega inteira de vinho jovem. O recolhimento existe em função
        do que está acima; sem nada acima, ele não tem função.
      */}
      {doDiaADia.length > 0 ? (
        (() => {
          const unicoGrupo = comMomento.length === 0;
          const aberto = unicoGrupo || mostrarDoDiaADia;

          return (
            <section className="space-y-2 border-t border-adega-border pt-4">
              <button
                type="button"
                className="flex w-full items-baseline justify-between gap-3 text-left"
                disabled={unicoGrupo}
                onClick={() => setMostrarDoDiaADia((antes) => !antes)}
              >
                <span>
                  <span
                    className={
                      unicoGrupo ? 'font-semibold text-adega-gold' : 'font-semibold text-adega-muted'
                    }
                  >
                    Do dia a dia ({doDiaADia.length})
                  </span>
                  <span className="block text-xs text-adega-muted">
                    {unicoGrupo
                      ? 'Sem momento a perder: abra quando quiser.'
                      : 'Sem momento a perder: abra quando quiser. Ficam fora da ordem acima para não disputar atenção com quem tem platô a acertar.'}
                  </span>
                </span>
                {unicoGrupo ? null : (
                  <span aria-hidden className="shrink-0 text-adega-muted">
                    {mostrarDoDiaADia ? '−' : '+'}
                  </span>
                )}
              </button>

              {aberto ? (
                <>
                  <p className="text-xs text-adega-muted">Safra mais antiga primeiro.</p>
                  <Garrafas garrafas={doDiaADia} />
                </>
              ) : null}
            </section>
          );
        })()
      ) : null}
    </div>
  );
}

/**
 * Junta garrafas iguais (mesmo produtor, nome e safra) num só cartão, mantendo a
 * ordem de urgência que veio do backend: o grupo aparece onde está a primeira.
 */
/** A lista de cartões, com garrafas iguais já reunidas num cartão só. */
function Garrafas({ garrafas }: { garrafas: BottleDTO[] }) {
  return (
    <>
      {agruparIguais(garrafas).map((iguais) => {
        const primeira = iguais[0] as BottleDTO;
        return (
          <BottleCard
            key={primeira.id}
            bottle={primeira}
            iguais={iguais}
            extra={<JanelaResumo bottle={primeira} />}
          />
        );
      })}
    </>
  );
}

function agruparIguais(garrafas: BottleDTO[]): BottleDTO[][] {
  const grupos = new Map<string, BottleDTO[]>();
  for (const garrafa of garrafas) {
    const chave = chaveDoVinho(garrafa);
    grupos.set(chave, [...(grupos.get(chave) ?? []), garrafa]);
  }
  return [...grupos.values()];
}

/**
 * "Pico 2026 · até 2028" + o motivo curto: é o que explica a posição na lista.
 * Vinho jovem vira "beber jovem · até 2027" — o pico dele é o começo da janela,
 * e mostrá-lo sugeriria uma evolução que ele não tem.
 */
function JanelaResumo({ bottle }: { bottle: BottleDTO }) {
  const jovem = bottle.drinkProfile === 'beber_jovem';
  const partes = [
    jovem ? 'beber jovem' : null,
    bottle.drinkFrom && bottle.drinkFrom > new Date().getFullYear() ? `a partir de ${bottle.drinkFrom}` : null,
    !jovem && bottle.drinkPeak ? `pico ${bottle.drinkPeak}` : null,
    bottle.drinkUntil ? `até ${bottle.drinkUntil}` : null,
  ].filter(Boolean);

  const idade = idadeSemAnaliseQuePedeAtencao(bottle);
  if (idade !== null) {
    return (
      <p className="mt-1.5 text-xs font-medium text-amber-300">
        Safra {bottle.vintage}, {idade} anos e sem análise: pode estar no fim da janela.
      </p>
    );
  }

  if (partes.length === 0 && !bottle.drinkNote) {
    return null;
  }

  return (
    <div className="mt-1.5 space-y-0.5">
      {partes.length > 0 ? (
        <p className="text-xs font-medium text-adega-gold">{partes.join(' · ')}</p>
      ) : null}
      {bottle.drinkNote ? (
        <p className="line-clamp-2 text-xs text-adega-muted">{bottle.drinkNote}</p>
      ) : null}
    </div>
  );
}
